import { getCountryConsentType } from '$components/marketing-consent/helpers';
import { AccessDeniedPage } from '$pages/access-denied';
import { completeAccount } from '$pages/variants/complete-account';
import {
  AccessDeniedOutcome,
  CloseModalOutcome,
  UserCreatedOutcome,
  UserCreationFailedOutcome,
} from '$pages/variants/complete-account/controller';
import { AppEvents } from '$scripts/events/events';
import { CriticalOutcome } from '$scripts/outcome';
import { SocialProvider } from '$shared/types/social-provider';
import { getCookiesState } from '$store/cookies-store';
import { getCountriesState } from '$store/countries-store';
import { criticalErrorFlow } from '..';
import { FlowConfig as CompleteAccountConfig } from './schema';
import { getLoggers } from '$shared/loggers';

export type CompleteAccountFields = {
  firstName: string;
  lastName: string;
  email: string;
};

export async function completeAccountFlow(
  events: AppEvents,
  config: CompleteAccountConfig,
  params: {
    fields: Partial<CompleteAccountFields>;
    token: string;
    socialProvider: SocialProvider;
    client_id: string;
  }
): Promise<any> {
  const { logInfo } = getLoggers();
  logInfo('complete-account-flow started');

  const { countries, nearCountryId, nearCountryAgeOfConsent } =
    await getCountriesState();

  const { isCoppaCompliant } = getCookiesState();

  if (!isCoppaCompliant()) {
    logInfo('[complete-account-flow] Coppa compliance failed, navigating to Access Denied page');
    const outcome = await AccessDeniedPage.create({});
    return outcome;
  }

  logInfo('[complete-account-flow] navigating to complete account page');
  const outcome = await completeAccount.create({
    countries,
    isTheUserOldEnoughToConsent: true,
    selectedCountry: nearCountryId,
    ageOfConsent: nearCountryAgeOfConsent,
    consentProfile: config.consentProfile,
    form: {
      firstName: params.fields.firstName || '',
      lastName: params.fields.lastName || '',
      email: params.fields.email || '',
      monthOfBirth: 0,
      yearOfBirth: 0,
    },
    token: params.token,
    hasUserConsented: !(
      getCountryConsentType(nearCountryId, config.consentProfile) === 'explicit'
    ),
  });

  if (outcome.of(UserCreatedOutcome)) {
    logInfo('[complete-account-flow] UserCreatedOutcome received');
    // User login
    // Give token
    return outcome;
  }

  if (outcome.of(UserCreationFailedOutcome)) {
    logInfo('[complete-account-flow] UserCreationFailedOutcome received, invoking criticalErrorFlow');
    const { error } = outcome.getValue();
    return criticalErrorFlow(events, new CriticalOutcome(error));
  }

  if (outcome.of(AccessDeniedOutcome)) {
    logInfo('[complete-account-flow] AccessDeniedOutcome received, navigating to Access Denied page');
    const outcome = await AccessDeniedPage.create({});
    return outcome;
  }

  if (outcome.of(CloseModalOutcome)) {
    logInfo('[complete-account-flow] CloseModalOutcome received');
    return outcome;
  }

  logInfo('[complete-account-flow] ended with unhandled outcome');
  return outcome;
}
